import { ReactFlowInstance, Edge, UpdateNodeInternals, Node, Connection } from "reactflow";
import { IBannerContext } from "@plex/react-components";
import { IControlFlowParam, IDataParam, INodeTypeDefinition, ISchemaPickerOptions, NodeType } from "../NodeTypes/Base";
import {
  IConnectionValidationResult,
  checkAndRemoveUnmappable,
  dataHandleIsInput,
  dataHandleIsOutput,
  deleteEdgesFromNodes,
  getDataTypeFromDataHandleId,
  getDesignerControlEdge,
  getDesignerDataEdge,
  getInputHandle,
  getNameFromControlHandleId,
  getNameFromDataHandleId,
  getOutputHandle,
  validateConnection
} from "../Util/EdgeUtil";
import {
  camelCaseToReadable,
  canAddNode,
  getAvailableConfigDataTypes,
  isMenuVisible,
  updateNodeDataProperties,
  updateNodeTimestamp
} from "../Util/NodeUtil";
import {
  DataType,
  convertToList,
  shouldConvertToList,
  isListType,
  isIdentifierLike
} from "../NodeTypes/TypeDefinitions";
import { INodeHandleProps } from "./AddNodeMenu";
import {
  IDocumentObjectSchema,
  IDocumentObjectSchemaProperty,
  propertyIsCompositeIdentifier,
  propertyIsIdentifier
} from "../FlowDocument/FlowDocumentModel";
import { getDataTypeFromSchema, getSchema } from "../NodeTypes/DataSchemas";
import { NodeConfigPropertyType } from "../FlowDocument/PropertyTypeDefinitions";

export interface IDataTypeConfig {
  configProperties?: { propertyName: string; propertyValue: any }[];
  dataType: DataType;
}

export interface IAutoConnectMeta {
  label: string;
  nodeType: string;
  handleId: string;
  isOutput: boolean;
  fromNodeHandle?: INodeHandleProps;
  text: string;
  configProperties?: IDataTypeConfig;
  onlyValidFromSecondaryImplicitConversion: boolean;
  createConnections?: Connection[];
  allowNodeSelection?: boolean;
  autoApplyFirstItem?: boolean;
}

interface IIdentifierScalar {
  compositePropertyName?: string;
  scalarPropertyName: string;
}

export class AddMenuNodePopulater {
  public static _createNodeNatural: (
    nodeType: string,
    category: string | null,
    api: string | null,
    apiAction: string | null,
    apiFamily: string | null,
    x: number,
    y: number,
    useExistingId: string,
    properties?: any,
    bannerContext?: IBannerContext,
    allowNodeSelection?: boolean
  ) => any;
  public static _reactFlowInstance: ReactFlowInstance<any, any>;
  public static _bannerContext: IBannerContext;
  public static _reactFlowEdgeSetter: React.Dispatch<React.SetStateAction<Edge<any>[]>>;
  public static _reactFlowUpdateNodeInternals: UpdateNodeInternals;
  public static _activeNodes: Node<any, string | undefined>[];

  public static nodeCreator(newNode: any) {
    this._reactFlowInstance.addNodes(newNode);
  }

  public static nodeCreatorAndLinker(
    node: any,
    otherParams: any,
    useExistingNode: boolean = false,
    properties: any = {},
    allowNodeSelection: boolean = true
  ): any {
    const newNode = useExistingNode
      ? node
      : this._createNodeNatural(
          node.type,
          "",
          "",
          "",
          "",
          node.position.x,
          node.position.y,
          node.id,
          properties,
          undefined,
          allowNodeSelection
        );
    newNode.selected = allowNodeSelection;

    if (!useExistingNode && otherParams?.handleId === "FlowErrorInHandle") {
      newNode.data.nodeProperties = {
        ...newNode.data.nodeProperties,
        noceConfigProperties: {},
        stopType: [
          {
            key: "Failure",
            value: "Failure"
          }
        ]
      };
    }

    if (!newNode) {
      console.log(`Node Creation failed for template ${node}`);
      return null;
    }

    updateNodeDataProperties(newNode);

    this._reactFlowUpdateNodeInternals(newNode);
    updateNodeTimestamp(newNode);

    return newNode;
  }

  public static getValidNodeTypesForGivenHandle(
    fromNodeId: string,
    fromNodeType: string,
    fromHandleId: string,
    experimentalModeState: boolean,
    reactFlowInstance: ReactFlowInstance
  ): Map<string, IAutoConnectMeta> {
    let entries: IAutoConnectMeta[] = [];
    const fakeNewNodeId = "NEW_NODE_1";
    const nodeDefinitions = globalThis.nodeTypeDefinitions
      .getDefinitions()
      .filter((d: INodeTypeDefinition) => isMenuVisible(d, experimentalModeState));

    nodeDefinitions.forEach((nodeDefinition: INodeTypeDefinition) => {
      if (!canAddNode(nodeDefinition.id, reactFlowInstance)) {
        return;
      }

      const handleConfigs: { [handleId: string]: IDataTypeConfig } = {};

      const getHandleRank = (validationResult: IConnectionValidationResult) => {
        if (validationResult.onlyValidFromSecondaryImplicitConversion) {
          return 2;
        }
        if (validationResult.onlyValidFromImplicitConversion) {
          return 1;
        }
        return 0;
      };

      let toNodeInputHandleIds = nodeDefinition.controlInputs.map((controlInput: IControlFlowParam) => {
        return getDesignerControlEdge(
          controlInput.name,
          fakeNewNodeId,
          getNameFromControlHandleId(fromHandleId),
          fromNodeId
        ).sourceHandle;
      });

      const getAvailableDataTypes = (dataParam: IDataParam) => {
        let availableDataTypes: IDataTypeConfig[] = [{ dataType: dataParam.type }];
        // Only add type options for non-reference types,
        //   since we're including reference data props for association only and not to dynamically change their type.
        if (dataParam.type !== DataType.OBJECTREFERENCE) {
          const typesFromConfig = getAvailableConfigDataTypes(nodeDefinition.id, dataParam.name);
          availableDataTypes = availableDataTypes
            .filter(
              (config: IDataTypeConfig) =>
                !typesFromConfig.some((typeFromConfig: IDataTypeConfig) => typeFromConfig.dataType === config.dataType)
            )
            .concat(typesFromConfig);
        }

        return availableDataTypes;
      };

      if (dataHandleIsOutput(fromHandleId)) {
        nodeDefinition.dataInputs.forEach((dataInput: IDataParam) => {
          const availableDataTypes = getAvailableDataTypes(dataInput);
          availableDataTypes.forEach((availableType: IDataTypeConfig) => {
            const hasListOptions = availableDataTypes.some(
              (config: IDataTypeConfig) => (config.configProperties?.length ?? 0) > 0 && isListType(config.dataType)
            );
            let dataType = availableType.dataType as DataType;
            if (shouldConvertToList(availableType.dataType, dataInput, hasListOptions)) {
              dataType = convertToList(availableType.dataType);
            }
            const toHandleId = getDesignerDataEdge(
              { name: dataInput.name, type: dataType },
              dataType,
              fakeNewNodeId,
              getNameFromDataHandleId(fromHandleId),
              fromNodeId
            ).sourceHandle;
            toNodeInputHandleIds.push(toHandleId);
            handleConfigs[toHandleId] = { ...availableType, dataType: dataType };
          });
        });
      }
      const connectableInputHandleIds = toNodeInputHandleIds
        .map((toNodeHandleId: string) => {
          return {
            ...validateConnection(
              {
                source: fakeNewNodeId,
                sourceHandle: toNodeHandleId,
                target: fromNodeId,
                targetHandle: fromHandleId
              },
              nodeDefinition.id,
              fromNodeType,
              reactFlowInstance
            ),
            handleId: toNodeHandleId
          };
        })
        .filter((validation: IConnectionValidationResult) => validation.valid && !validation.requiredDataConfig)
        .sort((a: IConnectionValidationResult, b: IConnectionValidationResult) => getHandleRank(a) - getHandleRank(b));
      if (connectableInputHandleIds.length > 0) {
        const handleId = connectableInputHandleIds[0]!.handleId;
        entries.push({
          label: nodeDefinition.label,
          nodeType: nodeDefinition.id,
          handleId: handleId,
          isOutput: false,
          text: nodeDefinition.label,
          configProperties: handleConfigs[handleId],
          onlyValidFromSecondaryImplicitConversion:
            connectableInputHandleIds[0]!.onlyValidFromSecondaryImplicitConversion
        });
      }

      let toNodeOutputHandleIds = nodeDefinition.controlOutputs.map((controlOutput: IControlFlowParam) => {
        return getDesignerControlEdge(
          getNameFromControlHandleId(fromHandleId),
          fromNodeId,
          controlOutput.name,
          fakeNewNodeId
        ).targetHandle;
      });
      if (dataHandleIsInput(fromHandleId)) {
        nodeDefinition.dataOutputs.forEach((dataOutput: IDataParam) => {
          const availableDataTypes = getAvailableDataTypes(dataOutput);
          availableDataTypes.forEach((availableType: IDataTypeConfig) => {
            const hasListOptions = availableDataTypes.some(
              (config: IDataTypeConfig) => (config.configProperties?.length ?? 0) > 0 && isListType(config.dataType)
            );
            let dataType = availableType.dataType as DataType;
            if (shouldConvertToList(availableType.dataType, dataOutput, hasListOptions)) {
              dataType = convertToList(availableType.dataType);
            }
            const toHandleId = getDesignerDataEdge(
              { name: getNameFromDataHandleId(fromHandleId), type: dataType },
              dataType,
              fromNodeId,
              dataOutput.name,
              fakeNewNodeId
            ).targetHandle;
            toNodeOutputHandleIds.push(toHandleId);
            handleConfigs[toHandleId] = { ...availableType, dataType: dataType };
          });
        });
      }
      const connectableOutputHandleIds = toNodeOutputHandleIds
        .map((toNodeHandleId: string) => {
          return {
            ...validateConnection(
              {
                source: fromNodeId,
                sourceHandle: fromHandleId,
                target: fakeNewNodeId,
                targetHandle: toNodeHandleId
              },
              fromNodeType,
              nodeDefinition.id,
              reactFlowInstance
            ),
            handleId: toNodeHandleId
          };
        })
        .filter((validation: IConnectionValidationResult) => validation.valid && !validation.requiredDataConfig)
        .sort((a: IConnectionValidationResult, b: IConnectionValidationResult) => getHandleRank(a) - getHandleRank(b));
      if (connectableOutputHandleIds.length > 0) {
        const handleId = connectableOutputHandleIds[0]!.handleId;
        entries.push({
          label: nodeDefinition.label,
          nodeType: nodeDefinition.id,
          handleId: handleId,
          isOutput: true,
          text: nodeDefinition.label,
          configProperties: handleConfigs[handleId],
          onlyValidFromSecondaryImplicitConversion:
            connectableOutputHandleIds[0]!.onlyValidFromSecondaryImplicitConversion
        });
      }
    });

    entries = entries.sort(
      (a: IAutoConnectMeta, b: IAutoConnectMeta) =>
        this.getRank(a.nodeType, a.onlyValidFromSecondaryImplicitConversion) -
        this.getRank(b.nodeType, b.onlyValidFromSecondaryImplicitConversion)
    );

    const result: Map<string, IAutoConnectMeta> = new Map<string, IAutoConnectMeta>();
    entries.forEach((entry: IAutoConnectMeta) => result.set(entry.label, entry));

    return result;
  }

  private static getIdentifierPropertyScalars = (
    schemaProperties: {
      [name: string]: IDocumentObjectSchemaProperty;
    },
    dataType: DataType,
    direction: "refToId" | "idToRef"
  ): IIdentifierScalar[] => {
    let identifierProperties: IIdentifierScalar[] = [];
    Object.keys(schemaProperties).forEach((propertyName: string) => {
      const schemaProperty = schemaProperties[propertyName]!;
      const isScalarIdentifier =
        schemaProperty &&
        propertyIsIdentifier(schemaProperty) &&
        getDataTypeFromSchema(schemaProperty.schema) === dataType;
      if (isScalarIdentifier) {
        identifierProperties.push({ scalarPropertyName: propertyName });
      }
      if (propertyIsCompositeIdentifier(schemaProperty) && direction === "idToRef") {
        const compositeSchema = getSchema(schemaProperty.schema) as unknown as IDocumentObjectSchema;
        if (compositeSchema) {
          Object.keys(compositeSchema.properties).forEach((compositeScalarPropertyName: string) => {
            if (
              compositeSchema.properties[compositeScalarPropertyName]?.schema &&
              getDataTypeFromSchema(compositeSchema.properties[compositeScalarPropertyName]!.schema) === dataType
            ) {
              identifierProperties.push({
                compositePropertyName: propertyName,
                scalarPropertyName: compositeScalarPropertyName
              });
            }
          });
        }
      }
    });

    return identifierProperties;
  };

  private static getReferenceToIdentifierEntries(
    fromNodeHandle: INodeHandleProps,
    fromSchemaId: string,
    toName: string,
    toDataType: DataType,
    toNodeHandle: INodeHandleProps,
    direction: "refToId" | "idToRef"
  ): IAutoConnectMeta[] {
    const entries: IAutoConnectMeta[] = [];

    const referenceSchema = getSchema(fromSchemaId) as unknown as IDocumentObjectSchema;
    const identifierScalars = this.getIdentifierPropertyScalars(referenceSchema.properties, toDataType, direction);

    identifierScalars.forEach((identifierScalar: IIdentifierScalar) => {
      let identifierProperty: IDocumentObjectSchemaProperty | undefined = undefined;
      if (identifierScalar.compositePropertyName) {
        const compositeProperty = referenceSchema.properties[identifierScalar.compositePropertyName];
        if (compositeProperty) {
          const compositeIdentifierSchema = getSchema(compositeProperty.schema) as unknown as IDocumentObjectSchema;
          identifierProperty = compositeIdentifierSchema.properties[identifierScalar.scalarPropertyName];
        }
      } else {
        identifierProperty = referenceSchema.properties[identifierScalar.scalarPropertyName];
      }

      if (identifierProperty) {
        const identifierLabel =
          identifierProperty.label ?? camelCaseToReadable(identifierScalar.scalarPropertyName).replaceAll("_", " ");
        const label = `Connect ${identifierLabel}`;
        const identifierScalarDataType = getDataTypeFromSchema(identifierProperty.schema);
        if (identifierScalarDataType) {
          const entry = {
            label: label,
            text: label,
            onlyValidFromSecondaryImplicitConversion: false,
            allowNodeSelection: false
          };
          const identifierPrefix = "IDENTIFIER_VALUE__";
          if (direction === "refToId") {
            const newConnection: Connection = {
              source: toNodeHandle.nodeId,
              sourceHandle: toNodeHandle.handleId,
              // Will use the new node id in place of this.
              target: "",
              targetHandle: getOutputHandle(identifierScalar.scalarPropertyName, identifierScalarDataType).toHandleId()
            };
            const toNamePart = toName.replace(identifierPrefix, "");
            const namesMatch =
              identifierScalar.scalarPropertyName === toName || identifierScalar.scalarPropertyName === toNamePart;
            entries.push({
              ...entry,
              nodeType: NodeType.objectProperties,
              handleId: getInputHandle("object", DataType.OBJECT).toHandleId(),
              isOutput: false,
              createConnections: [newConnection],
              autoApplyFirstItem: namesMatch
            });
          } else {
            const newConnectionToRef: Connection = {
              // Will use the new node id in place of this.
              source: "",
              sourceHandle: getInputHandle(identifierScalar.scalarPropertyName, identifierScalarDataType).toHandleId(),
              target: fromNodeHandle.nodeId,
              targetHandle: fromNodeHandle.handleId
            };
            const fromName = getNameFromDataHandleId(fromNodeHandle.handleId);
            const fromNamePart = fromName.replace(identifierPrefix, "");
            const toRefNamesMatch =
              identifierScalar.scalarPropertyName === fromName || identifierScalar.scalarPropertyName === fromNamePart;

            const fromNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(NodeType.convertToReference);
            const schemaPropertyConfig = fromNodeDefinition?.nodeConfigProperties.find(
              (p) => p.propertyType === NodeConfigPropertyType.SchemaProperty
            );
            let configProperties: IDataTypeConfig | undefined = undefined;
            if (schemaPropertyConfig) {
              const schemaPropertyOptions = schemaPropertyConfig.options as ISchemaPickerOptions;
              configProperties = {
                configProperties: [
                  { propertyName: schemaPropertyConfig?.name, propertyValue: identifierScalar.scalarPropertyName },
                  {
                    propertyName: schemaPropertyOptions.displayProperty,
                    propertyValue: camelCaseToReadable(identifierScalar.scalarPropertyName).replaceAll("_", " ")
                  }
                ]
              } as IDataTypeConfig;
            }

            entries.push({
              ...entry,
              nodeType: NodeType.convertToReference,
              handleId: getOutputHandle("result", DataType.OBJECTREFERENCE).toHandleId(),
              isOutput: true,
              fromNodeHandle: toNodeHandle,
              createConnections: [newConnectionToRef],
              autoApplyFirstItem: toRefNamesMatch,
              configProperties: configProperties
            });
          }
        }
      }
    });

    return entries;
  }

  public static getValidActionsForConnection(
    fromNodeHandle: INodeHandleProps,
    toNodeHandle: INodeHandleProps,
    reactFlowInstance: ReactFlowInstance
  ): Map<string, IAutoConnectMeta> {
    let entries: IAutoConnectMeta[] = [];
    const fromDataType: DataType = getDataTypeFromDataHandleId(fromNodeHandle.handleId) as DataType;
    const fromName: DataType = getNameFromDataHandleId(fromNodeHandle.handleId) as DataType;
    const toDataType: DataType = getDataTypeFromDataHandleId(toNodeHandle.handleId) as DataType;
    const toName: DataType = getNameFromDataHandleId(toNodeHandle.handleId) as DataType;
    const isReferenceToIdentifierConnection = fromDataType === DataType.OBJECTREFERENCE && isIdentifierLike(toDataType);
    const isIdentifierToReferenceConnection = isIdentifierLike(fromDataType) && toDataType === DataType.OBJECTREFERENCE;

    const fromNode = reactFlowInstance.getNode(fromNodeHandle.nodeId);

    if (fromNode) {
      if (isReferenceToIdentifierConnection && fromNode.data.nodeProperties.outputs[fromName]) {
        const fromSchemaId = fromNode.data.nodeProperties.outputs[fromName].schemaId;
        entries = this.getReferenceToIdentifierEntries(
          fromNodeHandle,
          fromSchemaId,
          toName,
          toDataType,
          toNodeHandle,
          "refToId"
        );
      }

      if (isIdentifierToReferenceConnection) {
        const toNode = reactFlowInstance.getNode(toNodeHandle.nodeId);
        if (toNode && toNode.data.nodeProperties.inputs[toName]) {
          const toSchemaId = toNode.data.nodeProperties.inputs[toName].schemaId;
          entries = this.getReferenceToIdentifierEntries(
            fromNodeHandle,
            toSchemaId,
            toName,
            fromDataType,
            toNodeHandle,
            "idToRef"
          );
        }
      }
    }

    const result: Map<string, IAutoConnectMeta> = new Map<string, IAutoConnectMeta>();
    entries.forEach((entry: IAutoConnectMeta) => result.set(entry.label, entry));

    return result;
  }

  private static getRank = (nodeType: string, onlyValidFromSecondaryImplicitConversion: boolean) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(nodeType)!;
    const nodeTypeCount = globalThis.nodeTypeDefinitions.getDefinitions().length + 1;
    const implicitConversionRank = (onlyValidFromSecondaryImplicitConversion ? 1 : 0) * nodeTypeCount;
    return nodeDefinition.menuOrder + implicitConversionRank;
  };

  public static deleteNode(deletingNodes: [any]) {
    const deletingEdges = this._reactFlowInstance
      .getEdges()
      .filter((e: Edge) => deletingNodes.some((n: Node<any>) => n.id === e.source || n.id === e.target));
    checkAndRemoveUnmappable(
      deletingEdges.map((edge: Edge) => {
        return { edge: edge, newSchema: undefined };
      }),
      () => {
        this._reactFlowInstance.setNodes((nds) =>
          nds.filter((n) => deletingNodes.filter((dn: any) => n.id === dn.id).length === 0)
        );
        deleteEdgesFromNodes(
          deletingNodes.map((n: Node<any>) => n.id),
          this._reactFlowInstance,
          this._reactFlowUpdateNodeInternals
        );
      },
      this._reactFlowInstance,
      this._reactFlowUpdateNodeInternals
    );

    return true;
  }
}
